import {
    Button,
    Flex,
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import { BusinessFocus } from './BusinessFocus';
import { ServicesWeProvide } from './ServicesWeProvide';
import * as S from './MainInformation.style';
import { ProjectsPublished } from './ProjectsPublished';
import { AboutDescription } from '../about-description/AboutDescription';
import { Divider } from './Divider';
import { Associations } from '../associations/Associations';
import type { LocationDataType } from '@modules/professional/type/contact-page-types';
import { Locations } from '../locations/Locations';
import { SocialLinks } from './SocialLinks';
import { YearFounded } from './YearFounded';
import { YearJoined } from './YearJoined';
import { ProfessionalPhoneNumbers } from './ProfessionalPhoneNumbers';
import { useCurrentTabBannerImage } from '~/modules/professional/hook/use-current-tab-banner-image';
import { useProfessionalInitialRouteData } from '~/modules/professional/hook/use-professional-initial-route-data';
import { ProductsListed } from './ProductsListed';
import { ExternalLinkLine } from '@archipro-design/icons';
import { useAppData } from '@modules/root';
import Availability from './Availability';
import SaveToDesignBoard from '@modules/design-board/component/SaveToDesignBoard';
import { useSaveToDesignBoard } from '@modules/design-board';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { EnquiryInline } from '~/modules/enquiry/component/enquiry-inline/EnquiryInline';
import { useEnquiryModal } from '~/modules-rocco/enquiry/hooks/use-enquiry-modal';
import { PublicKPI } from './PublicKPI';

interface MainInformationProps {
    locations: LocationDataType[];
    responseRate: number;
    calculateDistance: () => Promise<boolean>;
    showProducts: boolean;
    disabledSocialLinks?: boolean;
    disabledSendEnquiry?: boolean;
}

export const MainInformation: React.FC<MainInformationProps> = ({
    locations,
    calculateDistance,
    showProducts,
    disabledSocialLinks,
    disabledSendEnquiry,
}) => {
    const professional = useProfessionalInitialRouteData();
    const { Banners: banners } = professional;
    const bannerImage = useCurrentTabBannerImage(banners);
    const { mobile: isMobile } = useAppDisplayModeContext();
    const { css } = useStyles({
        hasBannerImage: !!bannerImage,
    });
    const { favouriteItem } = useSaveToDesignBoard({
        itemType: 'ProfessionalPin',
        itemId: professional.ID,
    });
    const { availableForWorkEnabled } = useAppData();
    const availableForWork =
        professional.AvailableForWork && availableForWorkEnabled;

    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(professional.Website, {
            libraryItemID: professional.ID,
            professionalID: professional.ID,
            professionalTitle: professional.Title,
        });

    const { openEnquiryModal } = useEnquiryModal({
        professional: professional,
        itemType: 'professional',
        siteTreeID: professional.ID,
        includeBranch: true,
    });

    return isMobile ? (
        <div className={css(S.MobileInfoContainer)}>
            <AboutDescription />
            <Flex column className={css(S.Contacts)}>
                <div className={css(S.EnquireContainer)}>
                    <Flex>
                        <Button
                            size={16}
                            color="dark"
                            className={css(S.EnquireButton)}
                            onClick={() => {
                                openEnquiryModal();
                            }}
                            variables={{
                                darkButtonBackgroundColorNormal: '#222222',
                                darkOutlinedButtonBorderNormal: `1px solid #222222`,
                            }}
                        >
                            {professional.primaryCTA?.primaryCTALabel ??
                                `SEND ENQUIRY`}
                        </Button>

                        <SaveToDesignBoard
                            heartButton={{
                                className: css(S.ButtonHeart),
                                color: 'primary',
                                variables: {
                                    iconPadding: 0,
                                    buttonPadding: 0,
                                    primaryNoBKGButtonBackgroundColorHover:
                                        'transparent',
                                },
                                size: 20,
                            }}
                            {...favouriteItem}
                        />
                    </Flex>
                </div>
                <Flex
                    gap="space-20"
                    className={css(S.SaveShareContainer)}
                    hAlign="center"
                    vAlign="start"
                >
                    <Button
                        as={'a'}
                        href={referralTrackingURL}
                        target="_blank"
                        size={16}
                        color="dark"
                        transparent
                        outlined
                        variables={{
                            buttonSize20LineHeight: '130%',
                            buttonPadding: pxToRem(15),
                        }}
                        className={css(S.ViewWebsiteButton)}
                        {...referralTrackingParams}
                    >
                        VISIT WEBSITE
                        <ExternalLinkLine
                            styles={{
                                width: pxToRem(16),
                                height: pxToRem(16),
                                '& > svg': {
                                    marginLeft: pxToRem(8),
                                    marginTop: pxToRem(-2),
                                },
                            }}
                        />
                    </Button>
                </Flex>
            </Flex>

            <BusinessFocus />
            <ServicesWeProvide />
            {showProducts ? <ProductsListed /> : <ProjectsPublished />}

            <YearFounded />
            <YearJoined />
            <PublicKPI />

            <Associations />

            {locations.length > 0 && (
                <Locations
                    locations={locations}
                    calculateDistance={calculateDistance}
                    professionalID={professional.ID}
                />
            )}
            <ProfessionalPhoneNumbers />
            {availableForWork && <Availability />}

            <SocialLinks />
        </div>
    ) : (
        <div className={css(S.MainInfoContainer)}>
            <Flex space="between">
                <div className={css(S.LeftSection)}>
                    <BusinessFocus />
                    <ServicesWeProvide />
                    {showProducts ? <ProductsListed /> : <ProjectsPublished />}
                    <Divider />
                </div>
                <div className={css(S.RightSection)}>
                    <AboutDescription />
                    <YearFounded />
                    <YearJoined />
                    <PublicKPI />
                    <Associations />
                    <SocialLinks disabledSocialLinks={disabledSocialLinks} />
                    <Locations
                        locations={locations}
                        calculateDistance={calculateDistance}
                        professionalID={professional.ID}
                    />
                    <ProfessionalPhoneNumbers />
                    {availableForWork && <Availability />}
                    <Divider />
                    <EnquiryInline
                        className="md:mt-10"
                        professional={professional}
                        disableSubmit={disabledSendEnquiry}
                        includeBranch
                        showCustomEnquiryText
                        submitButtonText={
                            professional.primaryCTA?.primaryCTALabel ??
                            'SEND ENQUIRY'
                        }
                        siteTreeID={professional.ID}
                        itemType="professional"
                    />
                </div>
            </Flex>
        </div>
    );
};
